export type ExcelSheet = {name: string; rows: string[][]};

export type ParseXlsxOptions = {
   /** Cap each sheet's rows. Undefined = all rows. */
   rowsPerSheet?: number;
   /** Cap the number of sheets. Undefined = all sheets. */
   sheetLimit?: number;
};

/**
 * Parse an xlsx Response body into sheets. Shared between the inline preview
 * (full workbook, all rows) and the card peek (first sheet, first ~5 rows) so
 * both paths pick up bug fixes (encoding, header handling) in one place.
 */
export const parseXlsxFromResponse = async (
   res: Response,
   opts: ParseXlsxOptions = {}
): Promise<ExcelSheet[]> => {
   const XLSX = await import("@e965/xlsx");
   const buffer = await res.arrayBuffer();
   const wb = XLSX.read(buffer, {type: "array"});
   const names =
      opts.sheetLimit !== undefined ? wb.SheetNames.slice(0, opts.sheetLimit) : wb.SheetNames;
   return names.map((name) => {
      const ws = wb.Sheets[name];
      const rows = XLSX.utils.sheet_to_json<string[]>(ws, {header: 1});
      return {
         name,
         rows: opts.rowsPerSheet !== undefined ? rows.slice(0, opts.rowsPerSheet) : rows,
      };
   });
};

export type TextPayload = {content: string; truncated: boolean};

/**
 * Read a text Response, stopping as soon as we reach `byteLimit` bytes so we
 * don't buffer huge artifacts. Aborts the underlying stream on truncation.
 */
export const parseTextFromResponse = async (
   res: Response,
   byteLimit: number
): Promise<TextPayload> => {
   const contentLength = Number(res.headers.get("content-length") ?? NaN);
   if (Number.isFinite(contentLength) && contentLength <= byteLimit) {
      return {content: await res.text(), truncated: false};
   }

   const reader = res.body?.getReader();
   if (!reader) {
      const blob = await res.blob();
      const truncated = blob.size > byteLimit;
      const slice = truncated ? blob.slice(0, byteLimit) : blob;
      return {content: await slice.text(), truncated};
   }

   const chunks: Uint8Array[] = [];
   let received = 0;
   let truncated = false;
   while (received < byteLimit) {
      const {done, value} = await reader.read();
      if (done) break;
      if (received + value.byteLength > byteLimit) {
         chunks.push(value.slice(0, byteLimit - received));
         received = byteLimit;
         truncated = true;
         break;
      }
      chunks.push(value);
      received += value.byteLength;
   }

   if (truncated) {
      await reader.cancel();
   } else {
      // Peek one more chunk to distinguish "exactly at limit" from "more left".
      const {done, value} = await reader.read();
      if (!done && value && value.byteLength > 0) truncated = true;
      await reader.cancel();
   }

   const combined = new Uint8Array(received);
   let offset = 0;
   for (const c of chunks) {
      combined.set(c, offset);
      offset += c.byteLength;
   }
   return {content: new TextDecoder().decode(combined), truncated};
};
